import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { productsApi } from "@redux/features/products/productsApi";
import BaseDropdown, { adaptInfiniteLoader } from "@reusables/dropdowns/BaseDropdown";
import BaseInfiniteLoader from "@reusables/dropdowns/BaseInfiniteLoader";

import { IntegrationGuard } from "@/components/reusables/BaseIntegrationGuard";
import BaseMaterialIconButton from "@/components/reusables/BaseMaterialIconButton";
import { Collapse, Fade, Typography } from "@mui/material";
import { isErrorWithMessage } from "@redux/api/query";
import { useAppDispatch } from "@redux/hooks";
import i18n from "i18next";
import { CornerDownRight, Pencil } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LineSchema, SOProduct } from "../../../../types";
import { AddProductFormSchemeTyping } from "../AddProductModal";
import { ProductAlternativeNameEdit } from "./ProductAlternativeNameEdit";

export interface ProductSelectionDropdownProps {
    selectedLine?: LineSchema;
    isOpen?: boolean;
}

export interface ProductSelectionDropdownHandle {
    focus: () => void;
}

const ProductSelectionDropdown = forwardRef<
    ProductSelectionDropdownHandle,
    ProductSelectionDropdownProps
>(function ProductSelectionDropdown({ selectedLine, isOpen }, ref): JSX.Element {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.orders.modals.addProduct",
    });

    const dispatch = useAppDispatch();
    const { control, setValue, setFocus } = useFormContext<AddProductFormSchemeTyping>();

    const [productsOptions, setProductsOptions] = useState<SOProduct[]>([]);
    const [isProductLazyLoadingFetching, setIsProductLazyLoadingFetching] = useState(false);
    const [isEditingAlternativeName, setIsEditingAlternativeName] = useState(false);

    const inputRef = useRef<HTMLInputElement | null>(null);

    // Expose focus method to parent component
    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
    }));

    // Hiding the editing field after the model has been reopened
    useEffect(() => {
        if (!isOpen) {
            setIsEditingAlternativeName(false);
        }
    }, [isOpen]);

    // Handle Enter key for barcode scanning
    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (selectedLine) return;
        if (e.key !== "Enter") return;

        const input = e.target as HTMLInputElement;
        const barcode = input.value.trim();
        if (!barcode) return;
        e.preventDefault();
        const result = await dispatch(
            productsApi.endpoints.getModuleProductsFull.initiate(
                {
                    filters: { search: barcode },
                    pagination: { page: 1, limit: 100 },
                    module: "sale",
                },
                { subscribe: false }
            )
        ).unwrap();

        const product = result?.payload?.find((p) => p.barcode === barcode);
        if (product) {
            setValue("product", product);
            setTimeout(() => {
                setFocus("quantity");
            }, 100);
        }
    };

    const alternativeName = useWatch({
        name: "alternative_name",
        control: control,
    });

    const selectedProduct = useWatch({
        control,
        name: "product",
    });

    return (
        <div>
            {isEditingAlternativeName ? (
                <ProductAlternativeNameEdit
                    // Passing initial value like this to ensure that RHF state is not editied
                    initialValue={alternativeName || selectedProduct?.name || ""}
                    onSave={(val) => {
                        if (val === selectedProduct?.name) {
                            setValue("alternative_name", null, {
                                shouldDirty: true,
                            });
                        } else {
                            setValue("alternative_name", val, {
                                shouldDirty: true,
                            });
                        }
                        setIsEditingAlternativeName(false);
                    }}
                    onCancel={() => setIsEditingAlternativeName(false)}
                />
            ) : (
                <Controller
                    name="product"
                    control={control}
                    render={({ field, fieldState }) => (
                        <div
                            onKeyDown={handleKeyDown}
                            ref={(el) => {
                                if (el) {
                                    const input = el.querySelector("input");
                                    inputRef.current = input;
                                }
                            }}
                        >
                            <BaseInfiniteLoader
                                fetch={(search, page, limit) => {
                                    setIsProductLazyLoadingFetching(true);
                                    dispatch(
                                        productsApi.endpoints.getModuleProductsFull.initiate(
                                            {
                                                filters: {
                                                    search,
                                                },
                                                pagination: {
                                                    page,
                                                    limit,
                                                },
                                                module: "sale",
                                            },
                                            {
                                                subscribe: false,
                                            }
                                        )
                                    )
                                        .unwrap()
                                        .then((res) => {
                                            setProductsOptions(res?.payload ?? []);
                                        })
                                        .catch((e) => {
                                            if (isErrorWithMessage(e)) {
                                                toast.error(e.message);
                                            } else {
                                                toast.error(
                                                    i18n.t("general.responses.somethingWentWrong")
                                                );
                                            }

                                            console.error(
                                                "An error occurred in the products lazy loader",
                                                e
                                            );
                                        })
                                        .finally(() => {
                                            setIsProductLazyLoadingFetching(false);
                                        });
                                }}
                                limit={100}
                                result={productsOptions}
                                isLoading={isProductLazyLoadingFetching}
                                skip={!!selectedLine}
                            >
                                {(infinity) => (
                                    <>
                                        <BaseDropdown
                                            {...field}
                                            {...fieldState}
                                            {...adaptInfiniteLoader(infinity)}
                                            label={
                                                <div className="flex items-center justify-between">
                                                    {/*Product field label*/}
                                                    <span>{t("fields.product.label") + " *"}</span>

                                                    {/*Alternative Mame button*/}
                                                    <Fade in={!!field.value}>
                                                        <span>
                                                            <IntegrationGuard slug="twenty_four_seven_office">
                                                                <BaseMaterialIconButton
                                                                    tip={t(
                                                                        "fields.product.alternative_name.tooltip"
                                                                    )}
                                                                    tooltipPlacement="top"
                                                                    icon={<Pencil />}
                                                                    size="tiny"
                                                                    style="dim"
                                                                    onClick={() =>
                                                                        setIsEditingAlternativeName(
                                                                            true
                                                                        )
                                                                    }
                                                                />
                                                            </IntegrationGuard>
                                                        </span>
                                                    </Fade>
                                                </div>
                                            }
                                            placeholder={t("fields.product.placeholder")}
                                            getter={{
                                                label: (opt) => opt.name,
                                                key: (opt) => opt.id,
                                                caption: (opt) => opt.code,
                                            }}
                                            filterOptions={(option) =>
                                                option.name +
                                                "|" +
                                                option.code +
                                                "|" +
                                                option.barcode
                                            }
                                            disabled={!!selectedLine}
                                            virtualize
                                            autocomplete
                                        />
                                    </>
                                )}
                            </BaseInfiniteLoader>
                        </div>
                    )}
                />
            )}

            {/* Visual indicator of the previous value */}
            <Collapse in={!!alternativeName && !isEditingAlternativeName}>
                <div className="mt-2">
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ opacity: 0.5 }}
                        className="flex items-center space-x-2"
                    >
                        <CornerDownRight size={12} />
                        <span>{alternativeName}</span>
                    </Typography>
                </div>
            </Collapse>
        </div>
    );
});

export default ProductSelectionDropdown;
